import {
  type CompletedForm,
  type CompletedFormSummary,
  FORM_PDF_MAX_BYTES,
  FORM_REFUSALS,
  type FormEntry,
  type FormField,
  type FormStarterKey,
  type FormTemplate,
  formTemplateListSchema,
  pendingFormReview,
} from "@rosc/contracts";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { referenceClient } from "@/lib/orpc/client";
import { queryClient } from "@/lib/query-client";
import {
  getVisitScope,
  useVisitScope,
  type VisitScope,
} from "@/lib/visit-queries";

// AI form completion (VITE_FORM_COMPLETION_ENABLED): the clinician's form
// templates and the forms filled for a visit. Values and identifiers live
// only on the server (encrypted) and in this sheet while it is open.

export type {
  CompletedForm,
  CompletedFormSummary,
  FormEntry,
  FormField,
  FormTemplate,
};
export { FORM_PDF_MAX_BYTES, pendingFormReview };

const templatesKey = (scope: VisitScope | null): readonly unknown[] => [
  "form-templates",
  scope?.uid,
  scope?.accountPublicId,
  scope?.generation,
];
const visitFormsKey = (
  scope: VisitScope | null,
  visitPublicId: string,
): readonly unknown[] => [
  "visit-forms",
  visitPublicId,
  scope?.uid,
  scope?.accountPublicId,
  scope?.generation,
];
const context = (scope: VisitScope | null) => ({
  expectedUserUid: scope?.uid,
});

export function useFormTemplates({ enabled = true } = {}) {
  const scope = useVisitScope();
  const query = useQuery({
    queryKey: templatesKey(scope),
    queryFn: async ({ signal }) =>
      formTemplateListSchema.parse(
        await referenceClient.listFormTemplates(
          { query: {} },
          { context: context(scope), signal },
        ),
      ),
    enabled: enabled && !!scope,
    staleTime: 30_000,
    retry: false,
  });
  return {
    items: query.data?.items ?? [],
    starters: query.data?.starters ?? [],
    loading: !!scope && enabled && query.isPending,
    error: query.isError,
  };
}

export function useVisitForms(visitPublicId: string, { enabled = true } = {}) {
  const scope = useVisitScope();
  const query = useQuery({
    queryKey: visitFormsKey(scope, visitPublicId),
    queryFn: async ({ signal }) =>
      (
        await referenceClient.listVisitForms(
          { params: { public_id: visitPublicId }, query: {} },
          { context: context(scope), signal },
        )
      ).items,
    enabled: enabled && !!scope && !!visitPublicId,
    staleTime: 10_000,
    retry: false,
  });
  const refresh = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: visitFormsKey(scope, visitPublicId),
      }),
    [scope, visitPublicId],
  );
  return { items: query.data ?? [], loading: query.isPending, refresh };
}

const refreshTemplates = () =>
  queryClient.invalidateQueries({ queryKey: templatesKey(getVisitScope()) });

const toBase64 = async (file: Blob): Promise<string> => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000)
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
};

export type UploadProblem = "type" | "size";
/** Why a file cannot be uploaded as a form, before anything is sent. */
export const uploadProblem = (file: File): UploadProblem | null => {
  if (
    file.type !== "application/pdf" &&
    !file.name.toLowerCase().endsWith(".pdf")
  )
    return "type";
  if (file.size > FORM_PDF_MAX_BYTES) return "size";
  return null;
};

export const createFormTemplate = async (
  input: { file: File } | { starterKey: FormStarterKey },
): Promise<FormTemplate> => {
  const scope = getVisitScope();
  const body =
    "file" in input
      ? {
          pdf: await toBase64(input.file),
          name:
            input.file.name.replace(/\.pdf$/i, "").slice(0, 120) || undefined,
        }
      : { starterKey: input.starterKey };
  const created = await referenceClient.createFormTemplate(
    { query: {}, body },
    { context: context(scope) },
  );
  await refreshTemplates();
  return created;
};

export const saveFormTemplate = async (
  publicId: string,
  body: { name: string; fields: FormField[] },
): Promise<FormTemplate> => {
  const updated = await referenceClient.updateFormTemplate(
    {
      params: { public_id: publicId },
      query: {},
      body: { ...body, ready: true },
    },
    { context: context(getVisitScope()) },
  );
  await refreshTemplates();
  return updated;
};

export const deleteFormTemplate = async (publicId: string) => {
  await referenceClient.deleteFormTemplate(
    { params: { public_id: publicId }, query: {} },
    { context: context(getVisitScope()) },
  );
  await refreshTemplates();
};

// How to fill: from the visit (with the selected sources), or only from
// what the clinician dictated (nothing of the visit is sent).
export type FormFillChoice =
  | { source: "visit"; sourceIds: readonly number[] }
  | { source: "dictation"; dictation: string; instructions?: string };

export const fillVisitForm = (
  visitPublicId: string,
  templatePublicId: string,
  choice: FormFillChoice,
): Promise<CompletedForm> =>
  referenceClient.createVisitForm(
    {
      params: { public_id: visitPublicId },
      query: {},
      body:
        choice.source === "dictation"
          ? {
              templatePublicId,
              source: "dictation",
              dictation: choice.dictation.trim(),
              ...(choice.instructions?.trim()
                ? { instructions: choice.instructions.trim() }
                : {}),
            }
          : {
              templatePublicId,
              sourceIds: [...choice.sourceIds].slice(0, 10),
            },
    },
    { context: context(getVisitScope()) },
  );

export const getVisitForm = (publicId: string): Promise<CompletedForm> =>
  referenceClient.getVisitForm(
    { params: { public_id: publicId }, query: {} },
    { context: context(getVisitScope()) },
  );

export const updateVisitForm = (
  publicId: string,
  body: {
    values: Record<string, string | null>;
    accepted: string[];
    identifiers: Record<string, string>;
    complete: boolean;
  },
): Promise<CompletedForm> =>
  referenceClient.updateVisitForm(
    { params: { public_id: publicId }, query: {}, body },
    { context: context(getVisitScope()) },
  );

/** Downloads the completed PDF in the browser. */
export const downloadVisitForm = async (publicId: string): Promise<void> => {
  const file = await referenceClient.getVisitFormPdf(
    { params: { public_id: publicId }, query: {} },
    { context: context(getVisitScope()) },
  );
  const binary = atob(file.data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([bytes], { type: file.mediaType }));
  const link = document.createElement("a");
  link.href = url;
  link.download = file.filename;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

export type RefusalKey = keyof typeof FORM_REFUSALS;
/** Which refusal an API error message is, to explain it in the UI. */
export const refusalOf = (error: unknown): RefusalKey | null => {
  const message = (error as { message?: string } | null)?.message;
  const entry = Object.entries(FORM_REFUSALS).find(
    ([, text]) => text === message,
  );
  return entry ? (entry[0] as RefusalKey) : null;
};
